import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Bot, ChevronDown, ExternalLink, Menu, Radar, ShieldCheck, X } from 'lucide-react'

const Navbar = ({ onOpenModal }) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navigation = [
    { label: 'Produtos', 
      target: '#',
      submenu: [
        { label: 'ERP', target: '/wibi-erp' },
        { label: 'Zartoo App', target: '/zartoo-app' },
        { label: 'App B2B', target: '/app-b2b' },
        { label: 'WiBi AI (BI)', target: 'https://wibi.com.br', external: true }
      ]
    },
    { label: 'Contato', target: '/contato' }
  ]

  const ctaClasses = "bg-[#FF6B35] text-white px-4 py-2 rounded-lg font-semibold shadow-lg shadow-[#FF6B35]/20 hover:bg-[#E65100] transition-all"

  return (
    <nav
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'top-0' : 'top-4'}`}
    >
      <div className={`flex justify-center ${isScrolled ? 'px-0' : 'px-4'}`}>
        <div
          className={`transition-all duration-300 ${
            isScrolled
              ? 'w-full bg-white/90 shadow-lg backdrop-blur-xl'
              : 'w-full max-w-7xl bg-white/10 border border-white/10 backdrop-blur-lg rounded-2xl'
          }`}
        >
        <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center h-20 px-4">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img
                src={isScrolled ? 'https://zartoo.s3.sa-east-1.amazonaws.com/zartoo-logo.png' : 'https://zartoo.s3.sa-east-1.amazonaws.com/zartoo-logo-branca.png'}
                alt="Zartoo"
                className="h-10 w-auto transition-all duration-300 drop-shadow-lg"
              />
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div key={item.label} className="relative group">
                {item.submenu ? (
                  <>
                    <button
                      className={`flex items-center gap-1 text-sm font-semibold transition-colors ${isScrolled ? 'text-slate-800 hover:text-zartoo-dark' : 'text-white hover:text-zartoo-sand'}`}
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <div className="absolute left-0 mt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 pt-2">
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden py-2">
                        {item.submenu.map((subItem) => (
                          subItem.external ? (
                            <a
                              key={subItem.label}
                              href={subItem.target}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-zartoo-dark transition-colors"
                            >
                              {subItem.label}
                              <ExternalLink className="w-3 h-3 opacity-50" />
                            </a>
                          ) : (
                            <Link
                              key={subItem.label}
                              to={subItem.target}
                              className="block px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-zartoo-dark transition-colors"
                            >
                              {subItem.label}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => navigate(item.target)}
                    className={`text-sm font-semibold transition-colors ${isScrolled ? 'text-slate-800 hover:text-zartoo-dark' : 'text-white hover:text-zartoo-sand'}`}
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-3">
            <a
              href="https://app.zartoo.com.br/login"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-4 py-2 rounded-lg font-semibold border ${isScrolled ? 'border-zartoo-dark text-zartoo-dark' : 'border-white text-white'} hover:bg-white/10 transition-colors`}
            >
              Área do cliente
            </a>
            <button
              onClick={() => onOpenModal ? onOpenModal() : navigate('/demonstracao')}
              className={`px-4 py-2 rounded-lg font-semibold ${isScrolled ? 'bg-zartoo-dark text-white hover:bg-zartoo-medium' : 'bg-white/10 text-white hover:bg-white/20'} transition-colors`}
            >
              Fale Conosco
            </button>
          </div>

          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg ${isScrolled ? 'text-gray-900' : 'text-white'}`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden bg-white shadow-xl rounded-2xl mt-3 border border-slate-100 overflow-hidden max-h-[80vh] overflow-y-auto"
            >
              <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100">
                <div className="p-2 rounded-xl bg-zartoo-light/10 text-zartoo-dark">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <p className="text-sm text-slate-700">Sistema segura para sua operação.</p>
              </div>
              <div className="flex flex-col p-4 space-y-2">
                {navigation.map((item) => (
                  <div key={item.label}>
                    {item.submenu ? (
                      <div className="space-y-1">
                        <button 
                          onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                          className="flex items-center justify-between w-full px-3 py-3 rounded-xl bg-slate-50 text-slate-800 font-semibold hover:bg-zartoo-light/10 transition-colors"
                        >
                          <span>{item.label}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                        </button>
                        {activeDropdown === item.label && (
                          <div className="pl-4 space-y-1 border-l-2 border-slate-100 ml-3">
                            {item.submenu.map((subItem) => (
                              subItem.external ? (
                                <a
                                  key={subItem.label}
                                  href={subItem.target}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-slate-600 font-medium hover:text-zartoo-dark transition-colors"
                                >
                                  {subItem.label}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <button
                                  key={subItem.label}
                                  onClick={() => {
                                    navigate(subItem.target);
                                    setIsMobileMenuOpen(false);
                                  }}
                                  className="block w-full text-left px-3 py-2 rounded-lg text-slate-600 font-medium hover:text-zartoo-dark transition-colors"
                                >
                                  {subItem.label}
                                </button>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          navigate(item.target);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center justify-between w-full px-3 py-3 rounded-xl bg-slate-50 text-slate-800 font-semibold hover:bg-zartoo-light/10 transition-colors"
                      >
                        <span>{item.label}</span>
                        {item.target === '/wibi-erp' ? <Bot className="w-4 h-4 text-zartoo-dark" /> : <Radar className="w-4 h-4 text-zartoo-dark" />}
                      </button>
                    )}
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <a
                    href="https://app.zartoo.com.br/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center border-2 border-zartoo-dark text-zartoo-dark px-4 py-3 rounded-xl font-semibold hover:bg-zartoo-dark hover:text-white transition-all"
                  >
                    Área do cliente
                  </a>
                  <button
                    className="w-full bg-gradient-to-r from-zartoo-dark to-zartoo-medium text-white px-4 py-3 rounded-xl font-semibold hover:from-zartoo-medium hover:to-zartoo-light transition-all"
                    onClick={() => onOpenModal ? onOpenModal() : navigate('/demonstracao')}
                  >
                    Fale Conosco
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
